import React, { useEffect, useState } from 'react';
import { RecipeGeneratorPage } from './pages/RecipeGeneratorPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { HomePage } from './pages/HomePage';
import { LandingPage } from './pages/LandingPage';
import { RecipeViewOnly, ViewRecipe } from './components/RecipeViewOnly';
import { ShoppingCart, CartRecipe, CustomIngredient } from './components/ShoppingCart';
import { StoreShoppingList, AggregatedIngredient } from './components/StoreShoppingList';
import { DesignPreviewModal } from './components/DesignPreviewModal';
import { AlertCircle, ChevronUp } from 'lucide-react';
export type UserProfile = {
  name: string;
  avatar: string | null;
};
type AppView = 'landing' | 'onboarding' | 'home' | 'recipe-builder' | 'recipe-detail' | 'store-list';
const MOCK_RECIPE_DATA: ViewRecipe = {
  id: '1',
  thumbnail: '🥗',
  title: 'Mediterranean Quinoa Bowl',
  userRating: 0,
  averageRating: 4.5,
  totalRatings: 24,
  description: "A vibrant bowl featuring quinoa, roasted vegetables, chickpeas, and a tangy lemon-tahini dressing. Perfect for a light yet satisfying meal that's packed with nutrients and flavor.",
  prepTime: '25 min',
  cookTime: '15 min',
  difficulty: 'Easy',
  createdBy: 'You',
  createdByAvatar: '👨‍🍳',
  createdOn: '2024-01-15',
  defaultServings: 4,
  tags: ['Healthy', 'Vegetarian', 'Mediterranean', 'Quick'],
  ingredients: [{
    item: 'quinoa, uncooked',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'cherry tomatoes, halved',
    amount: 2,
    unit: 'cups'
  }, {
    item: 'cucumber, diced',
    amount: 1,
    unit: ''
  }, {
    item: 'chickpeas, drained',
    amount: 1,
    unit: 'can'
  }, {
    item: 'red onion, thinly sliced',
    amount: 0.5,
    unit: ''
  }, {
    item: 'feta cheese, crumbled',
    amount: 0.5,
    unit: 'cup'
  }, {
    item: 'olive oil',
    amount: 3,
    unit: 'tbsp'
  }, {
    item: 'lemon juice',
    amount: 2,
    unit: 'tbsp'
  }, {
    item: 'tahini',
    amount: 2,
    unit: 'tbsp'
  }, {
    item: 'garlic cloves, minced',
    amount: 2,
    unit: ''
  }, {
    item: 'fresh parsley, chopped',
    amount: 0.25,
    unit: 'cup'
  }],
  instructions: ['Cook quinoa according to package directions. Fluff with a fork and let cool to room temperature.', 'While quinoa cools, prepare the vegetables: halve cherry tomatoes, dice cucumber, and thinly slice red onion.', 'Drain and rinse chickpeas thoroughly. Pat dry with paper towels.', 'Make the dressing: In a small bowl, whisk together olive oil, lemon juice, tahini, minced garlic, salt, and pepper until smooth and creamy.', 'In a large bowl, combine cooled quinoa, prepared vegetables, chickpeas, and crumbled feta cheese.', 'Pour dressing over the quinoa mixture and toss gently to combine, ensuring everything is evenly coated.', 'Garnish with fresh chopped parsley. Serve immediately or refrigerate for up to 2 hours before serving.'],
  storage: 'Store in an airtight container in the refrigerator for up to 3-4 days. Keep dressing separate if meal prepping. The quinoa and vegetables will stay fresh, though the feta may soften slightly over time.',
  reheating: 'This dish is best served cold or at room temperature. If you prefer it warm, microwave individual portions for 1-2 minutes, stirring halfway through. Avoid overheating as it may make the vegetables mushy.',
  comments: [{
    id: '1',
    author: 'Alex',
    authorAvatar: '👩‍🍳',
    content: 'This was absolutely delicious! I added some roasted red peppers for extra flavor. Will definitely make again!',
    timestamp: '2024-01-16T10:30:00Z'
  }, {
    id: '2',
    author: 'Sam',
    authorAvatar: '🥘',
    content: 'Perfect for meal prep. I made a big batch on Sunday and it lasted all week. The tahini dressing is amazing!',
    timestamp: '2024-01-17T14:20:00Z'
  }]
};
export function App() {
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>({
    name: 'Chef Alex',
    avatar: '👨‍🍳'
  });
  const [currentView, setCurrentView] = useState<AppView>('landing');
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [previousView, setPreviousView] = useState<AppView | null>(null);
  const [hasSeenAnimation, setHasSeenAnimation] = useState(false);
  // Shopping cart state
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartRecipes, setCartRecipes] = useState<CartRecipe[]>([]);
  const [customIngredients, setCustomIngredients] = useState<CustomIngredient[]>([]);
  const [selectedStore, setSelectedStore] = useState<'kroger' | 'meijer' | 'walmart' | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [hasOpenedModal, setHasOpenedModal] = useState(false);
  const [shouldAnimateBanner, setShouldAnimateBanner] = useState(false);
  // Progressive pulsing states
  const [hasAddedToCart, setHasAddedToCart] = useState(false);
  const [hasOpenedCart, setHasOpenedCart] = useState(false);
  const [hasToggledIngredient, setHasToggledIngredient] = useState(false);
  const [hasVisitedStore, setHasVisitedStore] = useState(false);
  // Navigation helper that updates both state and browser history
  const navigateToView = (view: AppView, pushHistory: boolean = true) => {
    setCurrentView(view);
    if (pushHistory) {
      window.history.pushState({
        view
      }, '', `#${view}`);
    }
  };
  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.view) {
        setCurrentView(event.state.view);
      } else {
        setCurrentView('landing');
      }
    };
    window.addEventListener('popstate', handlePopState);
    // Set initial history state
    window.history.replaceState({
      view: currentView
    }, '', `#${currentView}`);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  // Start animating banner after 30 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasOpenedModal) {
        setShouldAnimateBanner(true);
      }
    }, 30000); // 30 seconds
    return () => clearTimeout(timer);
  }, [hasOpenedModal]);
  const handleOnboardingComplete = (profile: UserProfile) => {
    setUserProfile(profile);
    setHasCompletedOnboarding(true);
    navigateToView('landing');
  };
  const handleEditProfile = () => {
    navigateToView('onboarding');
  };
  const handleCreateRecipe = () => {
    navigateToView('recipe-builder');
  };
  const handleOpenRecipe = (recipeId: string) => {
    setSelectedRecipeId(recipeId);
    setPreviousView('home');
    navigateToView('recipe-detail');
  };
  const handleBackToHome = () => {
    navigateToView('home');
    setSelectedRecipeId(null);
    setPreviousView(null);
  };
  const handleNavigateLanding = () => {
    navigateToView('landing');
    setSelectedRecipeId(null);
    setPreviousView(null);
  };
  const handleNavigateHome = () => {
    if (currentView === 'recipe-detail' && (previousView === 'home' || previousView === 'recipe-builder')) {
      navigateToView('home');
      setSelectedRecipeId(null);
      setPreviousView(null);
    } else if (currentView === 'store-list') {
      navigateToView('home');
      setSelectedStore(null);
    } else {
      navigateToView('landing');
      setSelectedRecipeId(null);
      setPreviousView(null);
    }
  };
  const handleMakeRevision = (recipeId: string) => {
    navigateToView('recipe-builder');
  };
  const handleImportRecipe = () => {
    alert('Import recipe feature coming soon!');
  };
  const handleFinishRecipe = () => {
    setPreviousView('recipe-builder');
    setSelectedRecipeId('generated-recipe');
    navigateToView('recipe-detail');
  };
  const handleAddToCart = (recipeId: string, recipeName: string, thumbnail: string, defaultServings: number, ingredients: any[]) => {
    const existingRecipe = cartRecipes.find(r => r.id === recipeId);
    if (existingRecipe) {
      return;
    }
    const newRecipe: CartRecipe = {
      id: recipeId,
      name: recipeName,
      thumbnail,
      defaultServings,
      servings: defaultServings,
      ingredients: ingredients.map(ing => ({
        ...ing,
        included: true
      }))
    };
    setCartRecipes(prev => [...prev, newRecipe]);
    setHasAddedToCart(true); // Stop add-to-cart pulsing, start cart pulsing
  };
  const handleUpdateServings = (recipeId: string, servings: number) => {
    setCartRecipes(prev => prev.map(r => r.id === recipeId ? {
      ...r,
      servings
    } : r));
  };
  const handleToggleIngredient = (recipeId: string, ingredientIndex: number) => {
    setCartRecipes(prev => prev.map(r => r.id === recipeId ? {
      ...r,
      ingredients: r.ingredients.map((ing, idx) => idx === ingredientIndex ? {
        ...ing,
        included: !ing.included
      } : ing)
    } : r));
    setHasToggledIngredient(true); // Stop ingredient pulsing, start Kroger pulsing
  };
  const handleRemoveRecipe = (recipeId: string) => {
    setCartRecipes(prev => prev.filter(r => r.id !== recipeId));
  };
  const handleAddCustomIngredient = (ingredient: CustomIngredient) => {
    setCustomIngredients(prev => [...prev, ingredient]);
  };
  const handleRemoveCustomIngredient = (id: string) => {
    setCustomIngredients(prev => prev.filter(i => i.id !== id));
  };
  const handleCheckout = (store: 'kroger' | 'meijer' | 'walmart') => {
    setSelectedStore(store);
    setIsCartOpen(false);
    setPreviousView('home');
    navigateToView('store-list');
    setHasVisitedStore(true); // Stop Kroger button pulsing
  };
  const handleBackFromStore = () => {
    setSelectedStore(null);
    navigateToView('home');
    setIsCartOpen(true);
    setPreviousView(null);
  };
  const handleOpenModal = () => {
    setIsPreviewModalOpen(true);
    setHasOpenedModal(true);
    setShouldAnimateBanner(false);
  };
  const getAggregatedIngredients = (): AggregatedIngredient[] => {
    const ingredientMap = new Map<string, {
      totalAmount: number;
      unit: string;
      recipes: Set<string>;
      thumbnail: string;
    }>();
    cartRecipes.forEach(recipe => {
      const servingMultiplier = recipe.servings / recipe.defaultServings;
      recipe.ingredients.forEach(ing => {
        if (ing.included) {
          const key = ing.item.toLowerCase();
          const existing = ingredientMap.get(key);
          if (existing) {
            existing.totalAmount += ing.amount * servingMultiplier;
            existing.recipes.add(recipe.name);
          } else {
            ingredientMap.set(key, {
              totalAmount: ing.amount * servingMultiplier,
              unit: ing.unit,
              recipes: new Set([recipe.name]),
              thumbnail: recipe.thumbnail
            });
          }
        }
      });
    });
    customIngredients.forEach(ing => {
      const key = ing.item.toLowerCase();
      const existing = ingredientMap.get(key);
      if (existing) {
        existing.totalAmount += ing.amount;
        existing.recipes.add('Custom');
      } else {
        ingredientMap.set(key, {
          totalAmount: ing.amount,
          unit: ing.unit,
          recipes: new Set(['Custom']),
          thumbnail: '📦'
        });
      }
    });
    return Array.from(ingredientMap.entries()).map(([item, data]) => ({
      item,
      description: `Fresh ${item} from the ${selectedStore === 'kroger' ? 'produce' : selectedStore === 'meijer' ? 'fresh' : 'grocery'} section`,
      thumbnail: data.thumbnail,
      totalAmount: parseFloat(data.totalAmount.toFixed(1)),
      unit: data.unit,
      price: `$${(Math.random() * 10 + 1).toFixed(2)}`,
      location: `${String.fromCharCode(65 + Math.floor(Math.random() * 10))}${Math.floor(Math.random() * 20 + 1)}`,
      inStock: Math.random() > 0.15,
      usedInRecipes: Array.from(data.recipes)
    }));
  };
  const getNavigationContext = (): 'home' | 'browse' => {
    if (currentView === 'recipe-detail' && (previousView === 'home' || previousView === 'recipe-builder')) {
      return 'browse';
    }
    if (currentView === 'store-list' && previousView === 'home') {
      return 'browse';
    }
    return 'home';
  };
  const navigationContext = getNavigationContext();
  return <>
      {/* Main content */}
      <div className="pb-10" data-id="element-0">
        {currentView === 'landing' && <LandingPage onGenerateRecipe={() => navigateToView('recipe-builder')} onImportRecipe={handleImportRecipe} onBrowseRecipes={() => navigateToView('home')} onManageGuests={() => navigateToView('onboarding')} hasSeenAnimation={hasSeenAnimation} onAnimationComplete={() => setHasSeenAnimation(true)} data-id="element-1" />}

        {currentView === 'store-list' && selectedStore && <StoreShoppingList store={selectedStore} ingredients={getAggregatedIngredients()} onBack={handleBackFromStore} onNavigateHome={handleNavigateHome} onNavigateLanding={handleNavigateLanding} onGenerateRecipe={() => navigateToView('recipe-builder')} onImportRecipe={handleImportRecipe} onBrowseRecipes={() => navigateToView('home')} onManageGuests={() => navigateToView('onboarding')} navigationContext={navigationContext} data-id="element-2" />}

        {currentView === 'onboarding' && <OnboardingPage onComplete={handleOnboardingComplete} initialData={userProfile} data-id="element-3" />}

        {currentView === 'home' && <>
            <HomePage userProfile={userProfile} onEditProfile={handleEditProfile} onCreateRecipe={handleCreateRecipe} onOpenRecipe={handleOpenRecipe} onAddToCart={handleAddToCart} onOpenCart={() => {
          setIsCartOpen(true);
          setHasOpenedCart(true); // Stop cart pulsing, start Kroger pulsing
        }} cartItemCount={cartRecipes.length} onNavigateHome={handleNavigateHome} onNavigateLanding={handleNavigateLanding} onGenerateRecipe={() => navigateToView('recipe-builder')} onImportRecipe={handleImportRecipe} onBrowseRecipes={() => navigateToView('home')} onManageGuests={() => navigateToView('onboarding')} shouldPulseCart={hasAddedToCart && !hasOpenedCart} data-id="element-4" />
            <ShoppingCart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} recipes={cartRecipes} customIngredients={customIngredients} onUpdateServings={handleUpdateServings} onToggleIngredient={handleToggleIngredient} onRemoveRecipe={handleRemoveRecipe} onAddCustomIngredient={handleAddCustomIngredient} onRemoveCustomIngredient={handleRemoveCustomIngredient} onCheckout={handleCheckout} shouldPulseKroger={hasOpenedCart && !hasVisitedStore} data-id="element-5" />
          </>}

        {currentView === 'recipe-builder' && <RecipeGeneratorPage userProfile={userProfile} onEditProfile={handleEditProfile} onBackToHome={handleFinishRecipe} onNavigateHome={handleNavigateHome} onGenerateRecipe={() => navigateToView('recipe-builder')} onImportRecipe={handleImportRecipe} onBrowseRecipes={() => navigateToView('home')} onManageGuests={() => navigateToView('onboarding')} data-id="element-6" />}

        {currentView === 'recipe-detail' && selectedRecipeId && <>
            <RecipeViewOnly recipe={MOCK_RECIPE_DATA} onBack={handleBackToHome} onMakeRevision={handleMakeRevision} currentUserAvatar={userProfile?.avatar || '👤'} currentUserName={userProfile?.name || 'User'} onAddToCart={handleAddToCart} onOpenCart={() => {
          setIsCartOpen(true);
          setHasOpenedCart(true); // Stop cart pulsing, start Kroger pulsing
        }} cartItemCount={cartRecipes.length} onEditProfile={handleEditProfile} onNavigateHome={handleNavigateHome} onNavigateLanding={handleNavigateLanding} onGenerateRecipe={() => navigateToView('recipe-builder')} onImportRecipe={handleImportRecipe} onBrowseRecipes={() => navigateToView('home')} onManageGuests={() => navigateToView('onboarding')} navigationContext={navigationContext} shouldPulseAddToCart={!hasAddedToCart} shouldPulseCart={hasAddedToCart && !hasOpenedCart} data-id="element-7" />
            <ShoppingCart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} recipes={cartRecipes} customIngredients={customIngredients} onUpdateServings={handleUpdateServings} onToggleIngredient={handleToggleIngredient} onRemoveRecipe={handleRemoveRecipe} onAddCustomIngredient={handleAddCustomIngredient} onRemoveCustomIngredient={handleRemoveCustomIngredient} onCheckout={handleCheckout} shouldPulseIngredient={hasOpenedCart && !hasToggledIngredient} shouldPulseKroger={hasToggledIngredient && !hasVisitedStore} data-id="element-8" />
          </>}

        {!['landing', 'store-list', 'onboarding', 'home', 'recipe-builder', 'recipe-detail'].includes(currentView) && <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center" data-id="element-9">
            <div className="text-center space-y-4" data-id="element-10">
              <p className="font-mono text-gray-600" data-id="element-11">Unknown View</p>
              <button onClick={() => navigateToView('landing')} className="px-6 py-3 bg-gray-900 text-white font-mono text-xs uppercase tracking-widest" data-id="element-12">
                Back to Home
              </button>
            </div>
          </div>}
      </div>

      {/* Disclaimer Banner - Footer itself shakes and lifts with 3s pause */}
      <button onClick={handleOpenModal} className={`fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-dashed border-gray-300 hover:bg-gray-50 transition-colors w-full ${shouldAnimateBanner ? 'animate-shake-lift' : ''}`} style={shouldAnimateBanner ? {
      willChange: 'transform'
    } : undefined} data-id="element-13">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2" data-id="element-14">
          <AlertCircle className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-15" />
          <p className="font-mono text-xs uppercase tracking-widest text-gray-500" data-id="element-16">
            Design Preview — Idea Demo Only
          </p>
          <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-17" />
        </div>
      </button>

      {/* Design Preview Modal */}
      <DesignPreviewModal isOpen={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} data-id="element-18" />
    </>;
}